<template>
  <div>
    <div class="space-y-6" v-if="studentRequests.length > 0">
      <div class="flex items-center justify-between gap-3 px-4 rounded-full bg-white h-12 !shadow text-lg">
        <span>{{ submitText }}</span>
        <button
          v-if="currentIndex === studentRequests.length - 1"
          type="button"
          class="rounded-sm bg-gray-100 hover:bg-gray-200 px-2 uppercase"
          @click="editRequestAction">
          Edit
        </button>
      </div>
      <div class="flex gap-3">
        <button
          class="flex items-center justify-center rounded-full bg-white w-12 h-12 !shadow disabled:!bg-gray-200"
          type="button"
          :disabled="currentIndex === 0"
          @click="moveIndexBy(-1)"
          v-if="canViewCstData">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          class="flex items-center justify-center rounded-full bg-white w-12 h-12 !shadow disabled:!bg-gray-200"
          type="button"
          :disabled="currentIndex === studentRequests.length - 1"
          @click="moveIndexBy(1)"
          v-if="canViewCstData">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
        <div class="flex items-center justify-center gap-3 px-4 rounded-full bg-white h-12 !shadow">
          <div class="rounded-full bg-sky-600 py-1 px-3 uppercase font-bold text-white"
            v-if="canViewCstData && currentIndex === studentRequests.length - 1">
            Current
          </div>
          <div class="rounded-full py-1 px-3 uppercase font-bold text-white"
            v-if="canViewCstData && currentIndex !== studentRequests.length - 1"
            style="background-color: rgb(46, 55, 71);">
            Past
          </div>
          <div class="flex items-center gap-1 text-lg">
            <div>{{ startDate }}</div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M18 12H6"
              />
            </svg>
            <div>{{ endDate }}</div>
          </div>
        </div>
        <div class="flex items-center justify-center px-4 rounded-full bg-white h-12 !shadow">
          <div class="px-2"
            v-for="(weekdayInitial, index) of weekdayInitials"
            :key="`${weekdayInitial}${index}`">
            <button
              class="focus:outline-none font-bold text-white rounded-full h-10 w-10 flex items-center justify-center"
              :class="{
                'ring-2 ring-gray-700': index === currentWeekdayIndex,
              }"
              :style="{
                background: directionGradient(getDirection(index)),
              }"
              type="button"
              @click="currentWeekdayIndex = index">
              {{ weekdayInitial }}
            </button>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-white !shadow rounded-md py-2 px-3 text-lg">
          <div class="text-xl font-bold mb-3">Inbound</div>
          <div class="space-y-3 text-lg mb-3"
            v-if="currentSchedule.pickup.exists">
            <div class="flex items-center gap-3">
              <div class="font-bold">
                Arrive Time
              </div>
              <span>{{ currentSchedule.pickup.arriveTime }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div class="font-bold">
                Program Start Time
              </div>
              <span>{{ currentSchedule.pickup.programStartTime }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div class="font-bold">
                Address
              </div>
              <span>{{ currentSchedule.pickup.address }}</span>
            </div>
            <div class="flex flex-col bg-gray-100 items-start p-1 gap-1">
              <div class="flex flex-row gap-3">
                <div class="font-bold">
                  Building
                </div>
                <span>{{ currentSchedule.pickup.building }}</span>
              </div>
              <div class="flex flex-row gap-3"
                v-if="currentSchedule.pickup.buildingAddress">
                <div class="font-bold">
                  Address
                </div>
                <span class="text-sm">{{ currentSchedule.pickup.buildingAddress }}</span>
              </div>
            </div>
          </div>
          <div class="space-y-3 text-lg mb-3"
            v-if="!currentSchedule.pickup.exists">
            None
          </div>
        </div>
        <div class="bg-white !shadow rounded-md py-2 px-3 text-lg">
          <div class="text-xl font-bold mb-3">Outbound</div>
          <div class="space-y-3 text-lg mb-3"
            v-if="currentSchedule.dropoff.exists">
            <div class="flex items-center gap-3">
              <div class="font-bold">
                Program End Time
              </div>
              <span>{{ currentSchedule.dropoff.programEndTime }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div class="font-bold">
                Depart Time
              </div>
              <span>{{ currentSchedule.dropoff.departTime }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div class="font-bold">
                Address
              </div>
              <span>{{ currentSchedule.dropoff.address }}</span>
            </div>
            <div class="flex flex-col bg-gray-100 items-start p-1 gap-1">
              <div class="flex flex-row gap-3">
                <div class="font-bold">
                  Building
                </div>
                <span>{{ currentSchedule.dropoff.building }}</span>
              </div>
              <div class="flex flex-row gap-3"
                v-if="currentSchedule.dropoff.buildingAddress">
                <div class="font-bold">
                  Address
                </div>
                <span class="text-sm">{{ currentSchedule.dropoff.buildingAddress }}</span>
              </div>
            </div>
          </div>
          <div class="space-y-3 text-lg mb-3"
            v-if="!currentSchedule.dropoff.exists">
            None
          </div>
        </div>
      </div>
      <div>
        <div class="space-y-3 text-lg">
          <div class="flex items-center gap-3">
            <div class="font-bold">Grade</div>
            <span>{{ grade }}</span>
          </div>
          <div class="flex items-center gap-3">
            <div class="font-bold">Program</div>
            <span>{{ program }}</span>
          </div>
          <div class="flex items-center gap-3" v-if="stateReportCodeType">
            <div class="font-bold">
              State Report Code
            </div>
            <span>{{ `${stateReportCodeType}:${stateReportCode}` }}</span>
          </div>
          <div class="flex items-center gap-3">
            <div class="font-bold">
              Student Release
            </div>
            <span>{{ studentRelease }}</span>
          </div>
        </div>
      </div>
      <div class="space-y-3">
        <div>
          <div class="text-lg inline-block font-bold"
            v-if="!accommodations">
            No accommodations
          </div>
        </div>
        <div class="text-lg space-y-3" v-if="accommodations">
          <div class="font-bold">
            Accommodations:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            {{ accommodations }}
          </div>
        </div>
        <div v-if="vestInfo">
          <SafetyVestInfo
            :vestInfo="vestInfo"
          />
        </div>
        <div>
          <div class="text-lg font-bold"
            v-if="!medicalNotes">
            No medical information
          </div>
        </div>
        <div class="text-lg space-y-3" v-if="medicalNotes">
          <div class="font-bold">
            Medical Information:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            {{ medicalNotes }}
          </div>
        </div>
        <div class="text-lg font-bold"
          v-if="!additionalNotes">
          No additional transportation information
        </div>
        <div class="text-lg space-y-3" v-if="additionalNotes">
          <div class="font-bold">
            Additional Transportation Information:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            <div v-for="note in additionalNotes" :key="note.id">
              <span class="font-italic font-bold pr-2">
                {{ note.id }}
              </span>
              <span>
                {{ note.value }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Spinner
      v-if="displayLoadingStudentRequest"
      message="Loading Request History"
      :size="16"
    />
    <div v-if="studentRequests.length === 0 && !displayLoadingStudentRequest">None</div>
  </div>
</template>

<script>
import { map, cloneDeep, camelCase, isEmpty } from 'lodash'
import DROPDOWN_LISTS from '../DropdownLists.json'
import ACCOMMODATIONS_MAP from '../AccommodationsMap.json'

import SafetyVestInfo from './common/SafetyVestInfo.vue'
import Spinner from '../Spinner.vue'

import { getScheduleRequest, getScheduleRequestStatus } from '../../services/api.js'
import time from '../../services/utils/time.js'

import settings from '../../services/settings'
import user from '../../services/user/user.js'
import events from '../../services/events.js'

const weekdaySlotsTemplate = [
  {
    id: 'sun',
    display: 'Sunday',
    data: undefined,
  },
  {
    id: 'mon',
    display: 'Monday',
    data: undefined,
  },
  {
    id: 'tue',
    display: 'Tuesday',
    data: undefined,
  },
  {
    id: 'wed',
    display: 'Wednesday',
    data: undefined,
  },
  {
    id: 'thu',
    display: 'Thursday',
    data: undefined,
  },
  {
    id: 'fri',
    display: 'Friday',
    data: undefined,
  },
  {
    id: 'sat',
    display: 'Saturday',
    data: undefined,
  },
]

const weekdayInitialsTemplate = ['Su', 'M', 'T', 'W', 'R', 'F', 'Sa']

export default {
  components: {
    SafetyVestInfo,
    Spinner,
  },
  props: {
    studentDbId: Number,
  },
  data() {
    return {
      studentRequests: [],
      currentIndex: 0,
      currentWeekdayIndex: this.getWeekdayIndex(),
      weekdayInitials: ['Su', 'M', 'T', 'W', 'R', 'F', 'Sa'],
      weekdaySlots: [],
      user: {},

      displayLoadingStudentRequest: true,
    }
  },
  eventCallbackIds: {},
  methods: {
    editRequestAction() {
      settings.requestForm.dbId = this.studentDbId
      this.$router.push({
        name: 'ChangeRequestForm',
      })
    },
    getWeekdayIndex() {
      const dayInt = new Date().getDay()
      if (dayInt === 0 || dayInt === 6) {
        return 0
      }
      return dayInt - 1
    },
    async getScheduleRequest(studentDbId) {
      if (isNaN(studentDbId) || studentDbId <= 0) {
        return
      }

      try {
        this.displayLoadingStudentRequest = true
        this.studentRequests = (await getScheduleRequest(studentDbId, {})).data
        this.displayLoadingStudentRequest = false
      }
      catch {
        this.displayLoadingStudentRequest = false
        return
      }

      this.studentRequests = this.studentRequests.map((element, index) => {
        element['id'] = index
        return element
      })
      this.currentIndex = this.studentRequests.length - 1

      if (this.currentStudentRequest) {
        this.$emit('currentRequestDbId', this.currentStudentRequest['db_id'])

        this.assignScheduleData(this.currentStudentRequest.schedule_request_weekdays)
        getScheduleRequestStatus(this.currentStudentRequest['db_id'])
          .then(requestStatus => {
            this.$emit('statusChange', requestStatus.data)
          })
      }
    },
    moveIndexBy(offset) {
      this.currentIndex += offset
      if (this.currentIndex < 0) {
        this.currentIndex = 0
      }
      if (this.currentIndex >= this.studentRequests.length) {
        this.currentIndex = this.studentRequests.length - 1
      }

      if (this.currentStudentRequest) {
        this.$emit('currentRequestDbId', this.currentStudentRequest['db_id'])

        this.assignScheduleData(this.currentStudentRequest.schedule_request_weekdays)
        getScheduleRequestStatus(this.currentStudentRequest['db_id'])
          .then(requestStatus => {
            this.$emit('statusChange', requestStatus.data)
          })
      }
    },
    assignScheduleData(weekdays) {
      this.weekdaySlots = cloneDeep(weekdaySlotsTemplate)
      for (var i = 0; i < this.weekdaySlots.length; ++i) {
        let index = weekdays.findIndex(
          element => element.weekday === this.weekdaySlots[i].id
        )
        if (index > -1) {
          this.weekdaySlots[i].data = weekdays[index]
        }
      }

      this.weekdayInitials = cloneDeep(weekdayInitialsTemplate)
      var satIndex = weekdays.findIndex(elem => elem.weekday === 'sat')
      var sunIndex = weekdays.findIndex(elem => elem.weekday === 'sun')
      if (satIndex < 0 && sunIndex < 0) {
        this.weekdaySlots.shift()
        this.weekdaySlots.pop()
        this.weekdayInitials.shift()
        this.weekdayInitials.pop()
      }
    },
    getDirection(index) {
      let direction = ''
      if (
        this.weekdaySlots[index].data?.is_pickup &
        this.weekdaySlots[index].data?.is_dropoff
      ) {
        direction = 'B'
      } else if (this.weekdaySlots[index].data?.is_pickup) {
        direction = 'I'
      } else if (this.weekdaySlots[index].data?.is_dropoff) {
        direction = 'O'
      }
      return direction
    },
    directionGradient(direction) {
      switch (direction) {
        case 'I':
          return 'linear-gradient(to right, #0080d4 50%, rgb(200, 200, 200) 50%)'; // Left blue, right grey
        case 'O':
          return 'linear-gradient(to right, rgb(200, 200, 200) 50%, #0080d4 50%)'; // Left grey, right blue
        case 'B':
          return '#0080d4'; // Full blue
        default:
          return 'rgb(200, 200, 200)'; // Full grey
      }
    },
    reloadRequestStatus() {
      console.log(this.currentStudentRequest['db_id'])
      getScheduleRequestStatus(this.currentStudentRequest['db_id'])
        .then(requestStatus => {
          this.$emit('statusChange', requestStatus.data)
        })
    },
  },
  computed: {
    canViewCstData() {
      return this.user?.can('view_internal_cst_data')
    },
    currentStudentRequest() {
      return this.studentRequests[this.currentIndex]
    },
    grade() {
      if (this.currentStudentRequest.grade) {
        return DROPDOWN_LISTS.gradeList[this.currentStudentRequest?.grade]
      } else {
        return ''
      }
    },
    program() {
      return this.currentStudentRequest?.program.description
    },
    studentRelease() {
      if (this.currentStudentRequest.student_release === 'I')
        return 'Release Independently'
      else if (this.currentStudentRequest.student_release === 'e2e')
        return 'Eye-to-Eye Release'
      else if (this.currentStudentRequest.student_release === 'h2h')
        return 'Hand-to-Hand Transfer'
      else if (this.currentStudentRequest.student_release === 'mse')
        return 'Must See Enter'
      else return 'Error'
    },
    stateReportCodeType() {
      return this.currentStudentRequest?.state_report_code_type
    },
    stateReportCode() {
      return this.currentStudentRequest?.state_report_code
    },
    address() {
      return this.currentStudentRequest?.address
    },
    pickupAddress() {
      return this.currentStudentRequest?.pickup_address
    },
    dropoffAddress() {
      return this.currentStudentRequest?.dropoff_address
    },
    additionalNotes() {
      if (this.currentStudentRequest.notes === undefined) return undefined

      let notesObj = JSON.parse(this.currentStudentRequest?.notes)
      for (const key in notesObj) {
        if (!notesObj[key]) {
          delete notesObj[key]
        }
      }
      if (!isEmpty(notesObj)) {
        return map(notesObj, (value, key) => {
          return {
            id: key,
            value: value,
          }
        })
      } else {
        return undefined
      }
    },
    medicalNotes() {
      return this.currentStudentRequest?.medical_notes
    },
    accommodations() {
      const infoNames = [
        'info_aide',
        'info_wheelchair',
        'info_solo',
        'info_enclosed_vehicle',
        'info_booster_seat',
        'info_safety_vest',
        'info_safety_belt_locking_cover',
        'info_car_seat',
      ]
      let values = []
      for (let infoName of infoNames) {
        const value = this.currentStudentRequest[infoName]
        if (value) {
          infoName = camelCase(infoName)
          if (infoName === 'infoAide') {
            if (value in ACCOMMODATIONS_MAP.infoAide) {
              values.push(ACCOMMODATIONS_MAP.infoAide[value])
            }
          } else {
            values.push(ACCOMMODATIONS_MAP[infoName])
          }
        }
      }
      if (values.length > 0) {
        return values.join(', ')
      }
      return ''
    },
    vestInfo() {
      if (!this.currentStudentRequest.vest_info) {
        return
      }
      return {
        needVestProvided: this.currentStudentRequest.vest_info['need_vest_provided'],
        manufacturer: this.currentStudentRequest.vest_info['manufacturer'],
        modelNumber: this.currentStudentRequest.vest_info['model_number'],
        vestSize: this.currentStudentRequest.vest_info['vest_size'],
      }
    },
    submitText() {
      const submitDate = this.currentStudentRequest?.created_at
        ? new Date(this.currentStudentRequest.created_at).toLocaleString(
            'en-US',
            {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }
          )
        : ''

      let submitUser = undefined
      if (this.currentStudentRequest.audit_user) {
        submitUser = this.currentStudentRequest?.audit_user
        this.$emit('currentRequesterName', `${submitUser.first_name} ${submitUser.last_name}`)
      }

      if (this.canViewCstData) {
        if (submitUser) {
          return `Request submitted ${submitDate} by ${submitUser.first_name} ${submitUser.last_name}.`
        } else {
          return `Request submitted ${submitDate}.`
        }
      } else {
        return `Request submitted ${submitDate}.${
          this.currentIndex === this.studentRequests.length - 1
            ? ' See an error? Click edit on the right.'
            : ''
        }`
      }
    },
    startDate() {
      if (this.currentStudentRequest?.requested_start_date) {
        return new Date(
          this.currentStudentRequest.requested_start_date
        ).toLocaleString('en-US', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          timeZone: 'UTC',
        })
      } else {
        return ''
      }
    },
    endDate() {
      if (this.currentStudentRequest?.requested_end_date) {
        let endDateExclusive = new Date(this.currentStudentRequest.requested_end_date)
        let endDateDisplay = new Date(endDateExclusive.getTime())
        endDateDisplay.setDate(endDateExclusive.getDate() - 1)
        return endDateDisplay.toLocaleString('en-US', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          timeZone: 'UTC',
        })
      } else {
        return 'No End Date'
      }
    },
    currentSchedule() {
      const ref = this.weekdaySlots[this.currentWeekdayIndex].data
      // const currentScheduleRequest = this.studentRequests[this.currentIndex]

      const currentSchedule = {
        pickup: {
          exists: ref?.is_pickup,
          programStartTime: ref?.is_pickup
            ? time.formatAMPM(ref.program_start_time)
            : '',
          arriveTime: ref?.is_pickup ? time.formatAMPM(ref.arrival_time) : '',
          address: ref?.pickup_address
            ? ref.pickup_address.apartment
              ? `${ref.pickup_address.street}, ${ref.pickup_address.apartment}, ${ref.pickup_address.city} ${ref.pickup_address.state}, ${ref.pickup_address.zip_code}`
              : `${ref.pickup_address.street}, ${ref.pickup_address.city} ${ref.pickup_address.state}, ${ref.pickup_address.zip_code}`
            : '--',
          building: ref?.pickup_building ? ref.pickup_building.name : '--',
          buildingAddress: ref?.pickup_building ? ref.pickup_building.address.apartment
              ? `${ref.pickup_building.address.street}, ${ref.pickup_building.address.apartment}, ${ref.pickup_building.address.city} ${ref.pickup_building.address.state}, ${ref.pickup_building.address.zip_code}`
              : `${ref.pickup_building.address.street}, ${ref.pickup_building.address.city} ${ref.pickup_building.address.state}, ${ref.pickup_building.address.zip_code}`
            : undefined,
        },
        dropoff: {
          exists: ref?.is_dropoff,
          programEndTime: ref?.is_dropoff
            ? time.formatAMPM(ref.program_end_time)
            : '',
          departTime: ref?.is_dropoff
            ? time.formatAMPM(ref.departure_time)
            : '',
          address: ref?.dropoff_address
            ? ref.dropoff_address.apartment
              ? `${ref.dropoff_address.street}, ${ref.dropoff_address.apartment}, ${ref.dropoff_address.city} ${ref.dropoff_address.state}, ${ref.dropoff_address.zip_code}`
              : `${ref.dropoff_address.street}, ${ref.dropoff_address.city} ${ref.dropoff_address.state}, ${ref.dropoff_address.zip_code}`
            : '--',
          building: ref?.dropoff_building ? ref.dropoff_building.name : '--',
          buildingAddress: ref?.dropoff_building ? ref.dropoff_building.address.apartment
              ? `${ref.dropoff_building.address.street}, ${ref.dropoff_building.address.apartment}, ${ref.dropoff_building.address.city} ${ref.dropoff_building.address.state}, ${ref.dropoff_building.address.zip_code}`
              : `${ref.dropoff_building.address.street}, ${ref.dropoff_building.address.city} ${ref.dropoff_building.address.state}, ${ref.dropoff_building.address.zip_code}`
            : undefined,
        },
      }
      return currentSchedule
    },
  },
  async created() {
    this.$options.eventCallbackIds.viewUpdated = events.register('viewUpdated', async () => {
      await this.getScheduleRequest(this.studentDbId)
    })

    this.user = await user.get()
    await this.getScheduleRequest(this.studentDbId)
  },
  //beforeUnmount() {
  // eslint-disable-next-line
  beforeDestroy() {
    events.remove('viewUpdated', this.$options.eventCallbackIds.viewUpdated)
  },
  watch: {
    studentDbId: async function (newValue) {
      await this.getScheduleRequest(newValue)
    },
    routeData: function (newValue) {
      this.routeMap = this.createRouteMap(newValue)
      this.mutateScheduleData(
        this.currentStudentRequest.schedule.schedule_weekdays,
        this.routeMap
      )
    },
  },
}
</script>
